import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const Turnstile = forwardRef(({ siteKey, formId = 'reg-form', autoExecute = false, renderOnLoad = true, onVerify = null }, ref) => {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const [isReady, setIsReady] = useState(false);
	const pendingVerifyRef = useRef(null);
	const isRenderingRef = useRef(false);
	const scriptPromiseRef = useRef(null);
	const errorRetryCountRef = useRef(0);

	const clear_form_token_state = (form) => {
		if (!form) {
			return;
		}
		delete form.dataset.turnstileToken;
		delete form.dataset.turnstileTimestamp;
		const tokenField = form.querySelector('input[name="cf-turnstile-response"]');
		if (tokenField) {
			tokenField.value = '';
		}
	};

	const schedule_turnstile_retry = (widgetId) => {
		if (!widgetId || errorRetryCountRef.current >= 2 || !pendingVerifyRef.current) {
			return;
		}
		errorRetryCountRef.current += 1;
		setTimeout(() => {
			if (!window.turnstile || !widgetId) {
				return;
			}
			try {
				if (typeof window.turnstile.reset === 'function') {
					window.turnstile.reset(widgetId);
				}
				if (typeof window.turnstile.execute === 'function') {
					window.turnstile.execute(widgetId);
				}
			} catch (e) {
			}
		}, 800);
	};

	const ensureScriptLoaded = () => {
		if (scriptPromiseRef.current) {
			return scriptPromiseRef.current;
		}
		if (typeof window.turnstile !== 'undefined') {
			setIsReady(true);
			scriptPromiseRef.current = Promise.resolve();
			return scriptPromiseRef.current;
		}
		const existingScript = document.querySelector('script[src*="challenges.cloudflare.com/turnstile/v0/api.js"]');
		if (existingScript) {
			scriptPromiseRef.current = new Promise((resolve) => {
				const check = () => {
					if (typeof window.turnstile !== 'undefined') {
						setIsReady(true);
						resolve();
						return;
					}
					setTimeout(check, 100);
				};
				check();
			});
			return scriptPromiseRef.current;
		}
		scriptPromiseRef.current = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
			script.async = true;
			script.defer = true;
			script.onload = () => {
				setIsReady(true);
				resolve();
			};
			script.onerror = (e) => {
				reject(e);
			};
			document.head.appendChild(script);
		});
		return scriptPromiseRef.current;
	};

	useEffect(() => {
		if (!siteKey) {
			return;
		}
		if (autoExecute || renderOnLoad) {
			ensureScriptLoaded().catch(() => {});
		}
	}, [siteKey]);

	useEffect(() => {
		if (!renderOnLoad) {
			return;
		}
		if (!isReady || !siteKey || !containerRef.current) {
			return;
		}

		const form = document.getElementById(formId);
		if (!form) {
			return;
		}

		if (!window.turnstile || typeof window.turnstile.render !== 'function') {
			return;
		}
		if (widgetIdRef.current || isRenderingRef.current) {
			return;
		}
		isRenderingRef.current = true;
		const widgetId = window.turnstile.render(containerRef.current, {
			sitekey: siteKey,
			execution: 'execute',
			callback: (token) => {
				errorRetryCountRef.current = 0;
				if (form) {
					form.dataset.turnstileToken = token;
					form.dataset.turnstileTimestamp = Date.now().toString();
					form.dataset.turnstileWidgetId = widgetId;
					
					// Add or update hidden field for form submission
					let tokenField = form.querySelector('input[name="cf-turnstile-response"]');
					if (!tokenField) {
						tokenField = document.createElement('input');
						tokenField.type = 'hidden';
						tokenField.name = 'cf-turnstile-response';
						form.appendChild(tokenField);
					}
					tokenField.value = token;
					
					// Dispatch event to notify that token is ready
					form.dispatchEvent(new CustomEvent('turnstileTokenReady', { detail: { token } }));
				}
				if (typeof onVerify === 'function') {
					onVerify(token);
				}
				if (pendingVerifyRef.current) {
					pendingVerifyRef.current.resolve(token);
					pendingVerifyRef.current = null;
				}
			},
			'error-callback': (error) => {
				console.error('Turnstile error:', error);
				if (form) {
					delete form.dataset.turnstileToken;
					const tokenField = form.querySelector('input[name="cf-turnstile-response"]');
					if (tokenField) {
						tokenField.value = '';
					}
				}
				schedule_turnstile_retry(widgetId);
				if (pendingVerifyRef.current) {
					pendingVerifyRef.current.reject(error);
					pendingVerifyRef.current = null;
				}
			},
			'expired-callback': () => {
				if (form) {
					delete form.dataset.turnstileToken;
					const tokenField = form.querySelector('input[name="cf-turnstile-response"]');
					if (tokenField) {
						tokenField.value = '';
					}
				}
				schedule_turnstile_retry(widgetId);
			}
		});

		widgetIdRef.current = widgetId;
		isRenderingRef.current = false;

		// Store widget ID in form
		if (form) {
			form.dataset.turnstileWidgetId = widgetId;
		}

		if (autoExecute && window.turnstile && widgetId) {
			window.turnstile.execute(widgetId);
		}

		return () => {
			if (widgetIdRef.current && window.turnstile) {
				try {
					window.turnstile.remove(widgetIdRef.current);
				} catch (e) {
					console.error('Error removing Turnstile widget:', e);
				}
			}
			widgetIdRef.current = null;
			isRenderingRef.current = false;
			if (pendingVerifyRef.current) {
				pendingVerifyRef.current.reject(new Error('Turnstile unmounted'));
				pendingVerifyRef.current = null;
			}
		};
	}, [isReady, siteKey, formId, autoExecute, onVerify, renderOnLoad]);

	useImperativeHandle(ref, () => ({
		execute: (timeoutMs = 10000) => {
			return ensureScriptLoaded().then(() => {
				if (!window.turnstile) {
					return Promise.reject(new Error('Turnstile not ready'));
				}
				const form = document.getElementById(formId);
				if (!form) {
					return Promise.reject(new Error('Form not found'));
				}
				if (!widgetIdRef.current && containerRef.current && typeof window.turnstile.render === 'function') {
					if (!isRenderingRef.current) {
						isRenderingRef.current = true;
						try {
							const widgetId = window.turnstile.render(containerRef.current, {
								sitekey: siteKey,
								execution: 'execute',
								callback: (token) => {
									errorRetryCountRef.current = 0;
									if (form) {
										form.dataset.turnstileToken = token;
										form.dataset.turnstileTimestamp = Date.now().toString();
										form.dataset.turnstileWidgetId = widgetId;
										let tokenField = form.querySelector('input[name="cf-turnstile-response"]');
										if (!tokenField) {
											tokenField = document.createElement('input');
											tokenField.type = 'hidden';
											tokenField.name = 'cf-turnstile-response';
											form.appendChild(tokenField);
										}
										tokenField.value = token;
										form.dispatchEvent(new CustomEvent('turnstileTokenReady', { detail: { token } }));
									}
									if (typeof onVerify === 'function') {
										onVerify(token);
									}
									if (pendingVerifyRef.current) {
										pendingVerifyRef.current.resolve(token);
										pendingVerifyRef.current = null;
									}
								},
								'error-callback': (error) => {
									console.error('Turnstile error:', error);
									if (form) {
										delete form.dataset.turnstileToken;
										const tokenField = form.querySelector('input[name="cf-turnstile-response"]');
										if (tokenField) {
											tokenField.value = '';
										}
									}
									schedule_turnstile_retry(widgetId);
									if (pendingVerifyRef.current) {
										pendingVerifyRef.current.reject(error);
										pendingVerifyRef.current = null;
									}
								},
								'expired-callback': () => {
									if (form) {
										delete form.dataset.turnstileToken;
										const tokenField = form.querySelector('input[name="cf-turnstile-response"]');
										if (tokenField) {
											tokenField.value = '';
										}
									}
									schedule_turnstile_retry(widgetId);
								}
							});
							widgetIdRef.current = widgetId;
							form.dataset.turnstileWidgetId = widgetId;
						} finally {
							isRenderingRef.current = false;
						}
					}
				}
				if (!widgetIdRef.current) {
					return Promise.reject(new Error('Turnstile widget not ready'));
				}
				if (pendingVerifyRef.current) {
					return pendingVerifyRef.current.promise;
				}
				clear_form_token_state(form);
				let resolveFn;
				let rejectFn;
				const promise = new Promise((resolve, reject) => {
					resolveFn = resolve;
					rejectFn = reject;
				});
				pendingVerifyRef.current = { promise, resolve: resolveFn, reject: rejectFn };
				try {
					if (typeof window.turnstile.reset === 'function') {
						window.turnstile.reset(widgetIdRef.current);
					}
					window.turnstile.execute(widgetIdRef.current);
				} catch (e) {
					pendingVerifyRef.current = null;
					return Promise.reject(e);
				}
				setTimeout(() => {
					if (pendingVerifyRef.current && pendingVerifyRef.current.promise === promise) {
						pendingVerifyRef.current.reject(new Error('Turnstile execute timeout'));
						pendingVerifyRef.current = null;
					}
				}, timeoutMs);
				return promise;
			});
		},
		reset: () => {
			if (window.turnstile && widgetIdRef.current) {
				try {
					window.turnstile.reset(widgetIdRef.current);
					return true;
				} catch (e) {
					return false;
				}
			}
			return false;
		}
	}), []);

	return <div id="turnstile-container" ref={containerRef} style={{ display: 'none' }}></div>;
});

export default Turnstile;
